import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Collapse,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  VideoLibrary,
  Photo,
  Remove,
  Add,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';

interface UsageTransaction {
  id: string;
  type: string;
  amount: number;
  balanceAfter: number;
  description: string;
  compressionJobId?: string;
  paymentId?: string;
  createdAt: string;
}

interface UsageHistoryProps {
  token: string | null;
}

const UsageHistory: React.FC<UsageHistoryProps> = ({ token }) => {
  const [transactions, setTransactions] = useState<UsageTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/credits/transactions?limit=100', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch transaction history');
      }

      const data = await response.json();
      // Filter for credit usage and refunds (negative amounts or refunds without payment IDs)
      const usageTransactions = data.filter((t: UsageTransaction) =>
        t.type === 'Deduction' || t.amount < 0 || (t.type === 'Purchase' && !t.paymentId)
      );
      setTransactions(usageTransactions);
    } catch (err) {
      console.error('Error fetching usage history:', err);
      setError('Failed to load usage history');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCreditsSpent = (amount: number) => {
    return `${Math.abs(amount)} Credits`;
  };

  const getActionIcon = (description: string, amount: number) => {
    // Check if this is a refund (positive amount without payment ID)
    if (amount > 0) {
      return <Add sx={{ fontSize: 16, color: 'success.main' }} />;
    }
    if (description.toLowerCase().includes('video')) {
      return <VideoLibrary sx={{ fontSize: 16, color: 'primary.main' }} />;
    } else if (description.toLowerCase().includes('photo')) {
      return <Photo sx={{ fontSize: 16, color: 'secondary.main' }} />;
    }
    return <Remove sx={{ fontSize: 16, color: 'text.secondary' }} />;
  };

  const getActionType = (description: string, amount: number) => {
    // Check if this is a refund (positive amount without payment ID)
    if (amount > 0) {
      return 'Refund';
    }
    if (description.toLowerCase().includes('video')) {
      return 'Video';
    } else if (description.toLowerCase().includes('photo')) {
      return 'Photo';
    }
    return 'Other';
  };

  const getQualityFromDescription = (description: string) => {
    const qualityMatch = description.match(/\((.*?)\s+quality\)/i);
    return qualityMatch ? qualityMatch[1] : 'Standard';
  };

  const toggleRowExpansion = (transactionId: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(transactionId)) {
      newExpandedRows.delete(transactionId);
    } else {
      newExpandedRows.add(transactionId);
    }
    setExpandedRows(newExpandedRows);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (transactions.length === 0) {
    return (
      <Box sx={{ px: 3, textAlign: 'center', py: 4 }}>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
          No usage history found
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Your compression activities will appear here
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, sm: 3 } }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
        Usage History
      </Typography>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
              {!isMobile && <TableCell sx={{ fontWeight: 600 }}>Credits</TableCell>}
              {isMobile && <TableCell sx={{ width: 48 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((transaction) => {
              const isExpanded = expandedRows.has(transaction.id);
              return (
                <React.Fragment key={transaction.id}>
                  <TableRow hover sx={{ cursor: isMobile ? 'pointer' : 'default' }}>
                    <TableCell
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, py: { xs: 1, sm: 2 } }}
                      onClick={isMobile ? () => toggleRowExpansion(transaction.id) : undefined}
                    >
                      {formatDate(transaction.createdAt)}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, py: { xs: 1, sm: 2 } }}
                      onClick={isMobile ? () => toggleRowExpansion(transaction.id) : undefined}
                    >
                      {isMobile ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                            {transaction.description}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {transaction.amount > 0 ? '+' : ''}{formatCreditsSpent(transaction.amount)} credits
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                          {getActionIcon(transaction.description, transaction.amount)}
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {transaction.description}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                              <Chip
                                label={getActionType(transaction.description, transaction.amount)}
                                size="small"
                                variant="outlined"
                                color={
                                  getActionType(transaction.description, transaction.amount) === 'Video' ? 'primary' :
                                  getActionType(transaction.description, transaction.amount) === 'Refund' ? 'success' : 'secondary'
                                }
                              />
                              {transaction.amount < 0 && (
                                <Chip
                                  label={getQualityFromDescription(transaction.description)}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </TableCell>
                    {!isMobile && (
                      <TableCell sx={{ fontSize: '1rem', py: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {transaction.amount > 0 ? (
                            <Add sx={{ color: 'success.main', fontSize: 16 }} />
                          ) : (
                            <Remove sx={{ color: 'error.main', fontSize: 16 }} />
                          )}
                          <Typography
                            sx={{
                              color: transaction.amount > 0 ? 'success.main' : 'error.main',
                              fontWeight: 500
                            }}
                          >
                            {transaction.amount > 0 ? '+' : ''}{formatCreditsSpent(transaction.amount)}
                          </Typography>
                        </Box>
                      </TableCell>
                    )}
                    {isMobile && (
                      <TableCell sx={{ width: 48, py: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpansion(transaction.id)}
                        >
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                  {isMobile && (
                    <TableRow>
                      <TableCell sx={{ py: 0, border: 0 }} colSpan={3}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 1, backgroundColor: 'grey.50' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                              {getActionIcon(transaction.description, transaction.amount)}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {transaction.amount > 0 ? (
                                  <Add sx={{ color: 'success.main', fontSize: 16 }} />
                                ) : (
                                  <Remove sx={{ color: 'error.main', fontSize: 16 }} />
                                )}
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: transaction.amount > 0 ? 'success.main' : 'error.main',
                                    fontWeight: 500
                                  }}
                                >
                                  {transaction.amount > 0 ? '+' : ''}{formatCreditsSpent(transaction.amount)} Credits
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Chip
                                label={getActionType(transaction.description, transaction.amount)}
                                size="small"
                                variant="outlined"
                                color={
                                  getActionType(transaction.description, transaction.amount) === 'Video' ? 'primary' :
                                  getActionType(transaction.description, transaction.amount) === 'Refund' ? 'success' : 'secondary'
                                }
                              />
                              {transaction.amount < 0 && (
                                <Chip
                                  label={getQualityFromDescription(transaction.description)}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default UsageHistory;
